// ── Helpers ────────────────────────────────────────────────────────────────
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const round2 = (n) => Math.round(n * 100) / 100;

// Format a quadratic ax² + bx + c as a readable string
const formatQuadratic = (a, b, c) => {
  let parts = [];

  // ax² term
  if (a === 1) parts.push("x²");
  else if (a === -1) parts.push("-x²");
  else parts.push(`${a}x²`);

  // bx term
  if (b === 1) parts.push("+ x");
  else if (b === -1) parts.push("- x");
  else if (b > 0) parts.push(`+ ${b}x`);
  else if (b < 0) parts.push(`- ${Math.abs(b)}x`);

  // c term
  if (c > 0) parts.push(`+ ${c}`);
  else if (c < 0) parts.push(`- ${Math.abs(c)}`);

  return parts.join(" ") + " = 0";
};

// ── Quadratic: Find the Zeros ───────────────────────────────────────────────

// Integer roots mode — build equation FROM roots so answer is always clean
export const generateQuadraticIntegerRoots = (rootMin = -10, rootMax = 10) => {
  const r1 = randInt(rootMin, rootMax);
  const r2 = randInt(rootMin, rootMax);

  // (x - r1)(x - r2) = x² - (r1+r2)x + r1*r2
  const a = 1;
  const b = -(r1 + r2);
  const c = r1 * r2;

  return {
    display: formatQuadratic(a, b, c),
    answers: [r1, r2],
    answerType: "roots",
    topic: "Quadratics",
    questionType: "Find the Zeros",
  };
};

// Any roots mode — randomize coefficients, filter out impossible questions
export const generateQuadraticAnyRoots = (
  aMin = 1, aMax = 3,
  bMin = -10, bMax = 10,
  cMin = -10, cMax = 10,
  maxAttempts = 50
) => {
  for (let i = 0; i < maxAttempts; i++) {
    const a = randInt(aMin, aMax);
    if (a === 0) continue;
    const b = randInt(bMin, bMax);
    const c = randInt(cMin, cMax);
    const discriminant = b * b - 4 * a * c;

    if (discriminant < 0) continue; // no real roots — skip

    const r1 = round2((-b + Math.sqrt(discriminant)) / (2 * a));
    const r2 = round2((-b - Math.sqrt(discriminant)) / (2 * a));

    return {
      display: formatQuadratic(a, b, c),
      answers: [r1, r2],
      answerType: "roots",
      topic: "Quadratics",
      questionType: "Find the Zeros",
    };
  }

  // Fallback to integer roots if we couldn't find valid coefficients
  return generateQuadraticIntegerRoots();
};

// ── Factoring: Simple Trinomials x² + bx + c ───────────────────────────────
// Build from two factors p and q so the answer is always clean
// x² + bx + c = (x + p)(x + q) where b = p+q, c = p*q

const formatSimpleTrinomial = (b, c) => {
  let parts = ["x²"];
  if (b === 1) parts.push("+ x");
  else if (b === -1) parts.push("- x");
  else if (b > 0) parts.push(`+ ${b}x`);
  else if (b < 0) parts.push(`- ${Math.abs(b)}x`);
  if (c > 0) parts.push(`+ ${c}`);
  else if (c < 0) parts.push(`- ${Math.abs(c)}`);
  return parts.join(" ");
};

const formatFactor = (n) => {
  if (n === 0) return "(x)(x)"; // edge case
  if (n > 0) return `(x + ${n})`;
  return `(x - ${Math.abs(n)})`;
};

export const generateSimpleTrinomial = (factorMin = -10, factorMax = 10) => {
  const p = randInt(factorMin, factorMax);
  const q = randInt(factorMin, factorMax);

  const b = p + q;
  const c = p * q;

  // Display as the expanded trinomial
  const display = formatSimpleTrinomial(b, c);

  // The factored answer: (x + p)(x + q)
  // Present factors in a consistent order (smaller first)
  const [f1, f2] = [p, q].sort((a, b) => a - b);

  return {
    display: `Factor: ${display}`,
    // Students enter the two factors as numbers (the values added to x)
    answers: [f1, f2],
    answerType: "factors",
    answerLabel: ["First factor value", "Second factor value"],
    hint: `Find two numbers that multiply to ${c} and add to ${b}`,
    topic: "Factoring",
    questionType: "Simple Trinomials",
  };
};

// ── Factoring: Trinomials with Leading Coefficient ax² + bx + c ────────────
// Build from factors: (px + q)(rx + s) where a=p*r, b=p*s+q*r, c=q*s

const formatTrinomial = (a, b, c) => {
  let parts = [];
  if (a === 1) parts.push("x²");
  else if (a === -1) parts.push("-x²");
  else parts.push(`${a}x²`);
  if (b === 1) parts.push("+ x");
  else if (b === -1) parts.push("- x");
  else if (b > 0) parts.push(`+ ${b}x`);
  else if (b < 0) parts.push(`- ${Math.abs(b)}x`);
  if (c > 0) parts.push(`+ ${c}`);
  else if (c < 0) parts.push(`- ${Math.abs(c)}`);
  return parts.join(" ");
};

export const generateLeadingCoefficientTrinomial = (factorMin = -6, factorMax = 6) => {
  // Pick p, q, r, s but keep a small (2-5) so questions stay reasonable
  const aOptions = [2, 3, 4, 5];
  const a = aOptions[randInt(0, aOptions.length - 1)];

  // Factor a into p*r
  const factors = [];
  for (let p = 1; p <= a; p++) {
    if (a % p === 0) factors.push([p, a / p]);
  }
  const [p, r] = factors[randInt(0, factors.length - 1)];

  const q = randInt(factorMin, factorMax);
  const s = randInt(factorMin, factorMax);
  if (q === 0 || s === 0) return generateLeadingCoefficientTrinomial(factorMin, factorMax); // retry

  const b = p * s + q * r;
  const c = q * s;

  // Factored form: (px + q)(rx + s)
  const f1 = `(${p === 1 ? "" : p}x ${q > 0 ? "+ " + q : "- " + Math.abs(q)})`;
  const f2 = `(${r === 1 ? "" : r}x ${s > 0 ? "+ " + s : "- " + Math.abs(s)})`;

  return {
    display: `Factor: ${formatTrinomial(a, b, c)}`,
    answerDisplay: `${f1}${f2}`,
    answers: [q, s, p, r],
    answerType: "leading-factors",
    hint: `Try ac = ${a * c}, find two numbers that multiply to ${a * c} and add to ${b}`,
    topic: "Factoring",
    questionType: "Trinomials with Leading Coefficient",
    // Store factors for checking
    p, q, r, s,
  };
};

// ── Factoring: Common Factors ax² + bx ──────────────────────────────────────
// ax² + bx = x(ax + b) — student identifies the common factor and what remains

export const generateCommonFactor = (coeffMin = 2, coeffMax = 10) => {
  const gcf = randInt(2, 6); // the common factor
  const a = randInt(coeffMin, coeffMax);
  const b = randInt(coeffMin, coeffMax);
  if (a === b) return generateCommonFactor(coeffMin, coeffMax); // retry for variety

  // Expression: gcf*a*x² + gcf*b*x = gcf*x(a*x + b)
  const A = gcf * a;
  const B = gcf * b;

  return {
    display: `Factor: ${A}x² + ${B}x`,
    answers: [gcf, a, b],
    answerType: "common-factor",
    hint: `What is the GCF of ${A} and ${B}?`,
    topic: "Factoring",
    questionType: "Common Factors",
    gcf, a, b,
  };
};

// ── Topic Registry ──────────────────────────────────────────────────────────
// As we add more topics, register them here

export const TOPICS = {
  Quadratics: {
    label: "Quadratics",
    questionTypes: {
      "Find the Zeros": {
        label: "Find the Zeros",
        modes: ["Integer Roots", "Any Roots"],
        generate: (mode, settings) => {
          if (mode === "Integer Roots") {
            return generateQuadraticIntegerRoots(
              settings.rootMin ?? -10,
              settings.rootMax ?? 10
            );
          } else {
            return generateQuadraticAnyRoots(
              settings.aMin ?? 1, settings.aMax ?? 3,
              settings.bMin ?? -10, settings.bMax ?? 10,
              settings.cMin ?? -10, settings.cMax ?? 10
            );
          }
        },
      },
    },
  },

  Factoring: {
    label: "Factoring",
    questionTypes: {
      "Simple Trinomials": {
        label: "Simple Trinomials (x² + bx + c)",
        modes: ["Integer Factors"],
        generate: (mode, settings) => generateSimpleTrinomial(
          settings.rootMin ?? -10,
          settings.rootMax ?? 10
        ),
      },
      "Trinomials with Leading Coefficient": {
        label: "Trinomials with Leading Coefficient (ax² + bx + c)",
        modes: ["Integer Factors"],
        generate: (mode, settings) => generateLeadingCoefficientTrinomial(
          settings.rootMin ?? -6,
          settings.rootMax ?? 6
        ),
      },
      "Common Factors": {
        label: "Common Factors (ax² + bx)",
        modes: ["Integer Factors"],
        generate: () => generateCommonFactor(),
      },
    },
  },
};
